/**
 * @module common/utils
 */

import { MINIMUM_PWD_SCORE, PAGINATION_PAGE_SIZE, ADMIN_ROOT } from 'common/variables';
import url from 'url';
import idx from 'idx';
import queryString from 'query-string';
import reduce from 'lodash/reduce';
import moment from 'moment-timezone';
import { getAPIQuery as getSortAPIQuery } from 'common/sorter/utils';
import { getAPIQuery as getSearchAPIQuery } from 'common/searcher/utils';
import { getAPIQuery as getFilterAPIQuery } from 'common/filter/utils';
import { getAPIQuery as getPagerAPIQuery } from 'common/pager/utils';
import { DEFAULT_PAGINATION_PAGE_SIZE } from 'common/pager/constants';

 /**
 * Different util functions reused accross the app.
 *
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */

/**
 * Since we want to have both filter name and value and our form field is a checkbox
 * (with a boolean value) then we're storing them joined with a hash in the field name.
 * Then before saving them in the store we need to revert this, so that we can save
 * the filters in an object. Also in some cases we have the filter name appended and
 * joined with a ^ sign, so we want to get rid of that as well.
 *
 * @param {Object} data - object with filter fields
 * @returns {String} url-parsed string
 */
function cleanupFilters(data) {
  const filtersData = {};
  let filterName = null;
  let filterValue = null;
  let key = null;

  for (const [k, v] of Object.entries(data)) {
    // get rid of the form name
    key = k.replace(/(\^.*)/, '');
    // NOTE: the filter name is a hack, sometimes we get a . from the backend
    // in the middle of the name, and this messes up redux-form naming
    // we replace all '.' with a '!' to mitigate this, and then replace it back
    // later when cleaning up the filters. Again, this is a huge and dirty hack
    // I've asked backend to clean this up, but it will take a large effort and we
    // don't have time right now.  We really should update this, and the specialization
    // filter names on the backend soon.
    // I'm going to go shower.  I feel dirty.
    filterName = key.split('#')[0].replace('!', '.');
    filterValue = key.split('#')[1];

    if (v) {
      if (filtersData[filterName]) {
        filtersData[filterName].push(filterValue);
      } else {
        filtersData[filterName] = [filterValue];
      }
    }
  }

  return filtersData;
}

/**
 * Create a query string with filters that we want applied.
 *
 * @param {Object} appliedFilters - object of filters to apply
 * @returns {String} url-parsed string
 */
function getFiltersString(appliedFilters) {
  return reduce(appliedFilters, (mem, filter, key) => {
    const values = filter.join(`&${key}=`, key);
    return `${mem}&${key}=${values}`;
  }, '');
}

/**
 * Create a filters object from query string
 *
 * @param {Object} appliedFilters - object of filters to apply
 * @returns {String} url-parsed string
 */
function getFiltersObject(filtersString) {
  const filters = {};

  if (!filtersString) {
    return filters;
  }

  const filtersStr = filtersString[0] === '&' ? filtersString : `&${filtersString}`;
  filtersStr.split('&').forEach((filter) => {
    if (!filter.length) {
      return;
    }

    const filterName = filter.split('=')[0].replace('.', '!');
    const filterValues = filter.split('=')[1].split(',');
    filters[filterName] = filters[filterName]
      ? [...filters[filterName], ...filterValues]
      : filterValues;
  });

  return filters;
}

function parseFiltersFromUrl(urlStr) {
  const { query } = url.parse(urlStr);
  const filterStr = idx(queryString.parse(query), _ => _.filters);
  const routeFilters = getFiltersObject(filterStr);
  return routeFilters;
}

function parsePageFromUrl(urlStr) {
  const { query } = url.parse(urlStr);
  const page = idx(queryString.parse(query), _ => _.page) * 1 || 1;
  return page;
}

function parseSearchFromUrl(urlStr) {
  const { query } = url.parse(urlStr);
  const search = idx(queryString.parse(query), _ => _.search) || '';
  return search;
}

/**
 * This is a reverse operation to the `cleanupFilters`. Get data from the store
 * and create the correct field names to put in the form.
 *
 * @param {Object} appliedFilters - object of filters to apply
 * @param {String} formName - name of the form
 * @returns {String} filters object used by redux-store to create Fields components
 */
function getFormattedFilters(appliedFilters, formName) {
  const filtersObject = {};

  for (const [k, v] of Object.entries(appliedFilters)) {
    // NOTE: the filter name is a hack, sometimes we get a . from the backend
    // in the middle of the name, and this messes up redux-form naming
    // we replace all '.' with a '!' to mitigate this, and then replace it back
    // later when cleaning up the filters. Again, this is a huge and dirty hack
    // I've asked backend to clean this up, but it will take a large effort and we
    // don't have time right now.  We really should update this, and the specialization
    // filter names on the backend soon.
    // I'm going to go shower.  I feel dirty.
    const fieldName = k.replace('.', '!');

    for (let i = 0; i < v.length; i += 1) {
      if (v.toString() === 'true') {
        filtersObject[`${fieldName}#true^${formName}`] = true;
      } else {
        filtersObject[`${fieldName}#${v[i]}^${formName}`] = true;
      }
    }
  }

  return filtersObject;
}

/**
 * This function calculates the current page number and total page numbers using
 * data sent by the API.
 *
 * @param {Object} paginationData - object with next/prev offsets and other data needed
 * @returns {Object} object with two keys - `currentPage` and `pageCount`
 */
function getPaginationValues(paginationData) {
  let currentPage;
  let pageCount;

  if (paginationData.previous === null) {
    currentPage = 1;
  } else if (paginationData.limit === 1) {
    currentPage = paginationData.next ? paginationData.next - 1 : paginationData.previous - 1;
  } else {
    currentPage = paginationData.next ? (paginationData.next / paginationData.limit) :
      ((paginationData.previous + paginationData.limit) / paginationData.limit) + 1;
  }

  if (paginationData.count <= paginationData.limit) {
    pageCount = 1;
  } else {
    pageCount = Math.ceil(paginationData.count / paginationData.limit);
  }

  return {
    currentPage,
    pageCount,
  };
}

/* Return whether app is running on a mobile browser. */
const isMobile = () => {
  if (typeof navigator === "undefined" || !navigator.userAgent) {
    return false;
  }

  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const pwdStrengthMeter = (pwd) => {
  if (typeof zxcvbn === "undefined") {
    return { score: 0 };
  }
  return zxcvbn(pwd);
}

/* Validate a password, enforcing minimum strength */
const validatePwdStrength = (pwd) => {
  const score = pwdStrengthMeter(pwd).score;
  if (score < MINIMUM_PWD_SCORE) {
    return 'Please provide a stronger password';
  }
  return '';
};

const calculatePageOffset = (page, pageSize = PAGINATION_PAGE_SIZE) => (
  (page - 1) * pageSize
);

/* Share link on the the given social network. */
const shareLink = (link, socialNetwork) => {
  let fullLink = ''
  if (socialNetwork == 'facebook') {
    fullLink = `https://www.facebook.com/sharer/sharer.php?u=${link}`
  } else if (socialNetwork == 'twitter') {
    fullLink = `https://twitter.com/intent/tweet?text=${link}`
  } else if (socialNetwork == 'linkedin') {
    fullLink = `https://www.linkedin.com/shareArticle?mini=true&url=${link}`;
  }
  window.open(fullLink, '_blank');
}

/* Modifies the case of the project title to look consistent */
const cleanProjectTitle = (title) => {
  if (title) {
    return title.replace(/\w\S*/g, function(txt){
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  } else {
    return "";
  }
}

/* Get time left label given a date/time string. */
const getTimeLeftLabel = (str, opts={}) => {
  const defaultOpts = {
    emptyLabel: '-',
    negativeLabel: 'Overdue',
    append: '',
    forceMax: true,
    daysUnit: 'd',
    hoursUnit: 'h',
    minutesUnit: 'm'
  };
  const mergedOpts = {...defaultOpts, ...opts};

  /* no time string provided */
  if (!str) {
    return mergedOpts.emptyLabel;
  }

  const hoursLeft = moment(str).diff(moment(), 'hours');
  const daysLeft = moment(str).diff(moment(), 'days');
  const minutesLeft = moment(str).diff(moment(), 'minutes');

  /* time left < 0 */
  if (moment().isAfter(str)) {
    return mergedOpts.negativeLabel;
  }

  let label = ''

  /* time left > 6 days */
  if (hoursLeft > 6*24 && mergedOpts.forceMax) {
    label = `7${mergedOpts.daysUnit}`;

  /* 2 days <= time left <= 6 days */
  } else if (hoursLeft >= 2*24) {
    label = `${daysLeft}${mergedOpts.daysUnit}`;

  /* 1 hour <= time left < 2 days */
  } else if (hoursLeft >= 1) {
    label = `${hoursLeft}${mergedOpts.hoursUnit}`;

  /* time left < 1 hour */
  } else {
    label = `${minutesLeft}${mergedOpts.minutesUnit}`;
  }

  return label + (mergedOpts.append? ` ${mergedOpts.append}`:'');
}

const isPhoneValid = (phone) => {
  if (typeof libphonenumber === "undefined") {
    return false;
  }

  const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();
  let phoneNumber = null;
  try {
    phoneNumber = phoneUtil.parse(phone, 'US');
  } catch (err) {
    return false;
  }
  return !!phoneNumber && phoneUtil.isValidNumber(phoneNumber);
}

/**
 * This function returns a canonical URL consisting of the domain name followed by the provided
 * urlSuffix. The purpose of this function is to avoid hardcoding the domain/protocol all over the
 * place.
 *
 * If the leading or trailing slash is missing from the `urlSuffix` it is added, so this function
 * also performs a little bit of cleanup to ensure consistent canonical URLs.
 *
 * @param {String} urlSuffix - the part that should come after the domain name construct the URL
 * @returns {String} - a URL that can be used as the value of the `href` attribute in the canonical
 * link META tag
 */
const getCanonicalURL = (urlSuffix) => {
  if (urlSuffix) {
    if (!urlSuffix.startsWith('/')) {
      urlSuffix = `/${urlSuffix}`;
    }
    if (!urlSuffix.endsWith('/')) {
      urlSuffix = `${urlSuffix}/`;
    }
    return `https://www.crowdspring.com${urlSuffix}`;
  } else {
    return `https://www.crowdspring.com`;
  }
}

/* Return whether a given URL is active, by comparing it with the current router pathname. */
const isUrlActive = (url, pathname, matchExact=false, regex=null) => {
  const urlWithSlash = !url.endsWith('/')? `${url}/` : url;
  const pathWithSlash = !pathname.endsWith('/')? `${pathname}/` : pathname;

  if (regex) {
    return !!pathname.match(regex);
  }

  if (matchExact) {
    return pathWithSlash === urlWithSlash;
  }

  return pathWithSlash.indexOf(urlWithSlash) !== -1;
}

// Used for XSS protection
const tagsToReplace = {
    '</script>': '',
    '<script>': ''
};

const replaceTag = (tag) => {
  return tagsToReplace[tag] || tag;
}

const safe_tags_replace = (str) => {
  return str.replace(/\<(\w)*\/(\w)*script/g, '');
}

/* Get API query string from sorting/search/filter/etc URL query parameters. */
const getAPIQueryString = (resourceName, query={}, opts={}) => {
  const {
    sortParamName='sort', searchParamName='search', pageSize=DEFAULT_PAGINATION_PAGE_SIZE,
    extraAPIQuery={}
  } = opts;
  const sortingAPIQuery = getSortAPIQuery(resourceName, sortParamName, query);
  const searchAPIQuery = getSearchAPIQuery(resourceName, searchParamName, query);
  const filterAPIQuery = getFilterAPIQuery(resourceName, query);
  const pagerAPIQuery = getPagerAPIQuery(resourceName, pageSize, query);

  return queryString.stringify({
    ...sortingAPIQuery,
    ...searchAPIQuery,
    ...filterAPIQuery,
    ...pagerAPIQuery,
    ...extraAPIQuery
  });
}

/* Utility that checks if the API query string for a given resource has changed. */
const hasAPIQueryStringChanged = (resourceName, prevProps, currentProps) => {
  const prevString = getAPIQueryString(resourceName, prevProps.location.query);
  const currentString = getAPIQueryString(resourceName, currentProps.location.query);
  const prevLocationKey = prevProps.location.key;
  const currentLocationKey = currentProps.location.key;
  return prevString !== currentString || prevLocationKey !== currentLocationKey;
}

/* Get last combined sorting/search/filter/etc query for a given resource */
const getLastResourceQuery = (resourceName, { sorter, searcher, filter, pager }) => {
  const lastSortQuery = sorter.lastQueries[resourceName] || {};
  const lastSearchQuery = searcher.lastQueries[resourceName] || {};
  const lastFilterQuery = filter.lastQueries[resourceName] || {};
  const lastPagerQuery = pager.lastQueries[resourceName] || {};

  return {
    ...lastSortQuery,
    ...lastSearchQuery,
    ...lastFilterQuery,
    ...lastPagerQuery
  };
}

/* FIXME: due to a bug with z-index + -webkit-overflow-scrolling on mobile Safari, disable smooth
   scrolling when modals are showing up.
   https://stackoverflow.com/questions/37696746/ios-not-respecting-z-index-with-webkit-overflow-scrolling */
const handleIosModalsOverlap = () => {
  if (navigator.userAgent.includes("iPad") || navigator.userAgent.includes("iPhone")) {
    setInterval(() => {
      if (!document.body) {
        return;
      }

      const disabledScrollStyle = "-webkit-overflow-scrolling: auto";
      const smoothScrollDisabled = (
        (document.body.getAttribute("style") || "").includes(disabledScrollStyle)
      );
      const hasModalsOpen = (
        document.getElementsByClassName("modal-dialog").length +
        document.getElementsByClassName("single-entry-modal").length +
        document.getElementsByClassName("add-item-form").length +
        document.getElementsByClassName("package-details").length
      ) > 0;

      if (hasModalsOpen) {
        if (!smoothScrollDisabled) {
          document.body.setAttribute("style", disabledScrollStyle);
        }
      } else {
        if (smoothScrollDisabled) {
          document.body.setAttribute("style", "");
        }
      }
    }, 500);
  }
}

const preventDefault = (func) => (e) => {
  if (e && e.preventDefault) {
    e.preventDefault();
  }

  func();
}

const removeExtraSpaces = (str) => {
  if (!str) {
    return str;
  }

  return str.replace(/\s+/g, ' ').trim();
}

const scrollToModalTop = (modalClassName) => {
  const modalEl = document.getElementsByClassName(modalClassName)[0];
  if (!modalEl) {
    console.warn(`No modal with class "${modalClassName}" found`);
    return;
  }

  const containerEl = modalEl.getElementsByClassName("modal-content-container")[0];
  if (!containerEl) {
    console.warn(`No scrolling container found for modal with class "${modalClassName}"`);
    return;
  }

  containerEl.scrollTop = 0;
}

const atAdmin = (state) => (
  (idx(state, _ => _.routing.locationBeforeTransitions.pathname) || '').startsWith(ADMIN_ROOT)
);

export {
  cleanupFilters, getFiltersString, getFiltersObject, getFormattedFilters, getPaginationValues,
  isMobile, validatePwdStrength, parseFiltersFromUrl, parsePageFromUrl, calculatePageOffset,
  shareLink, cleanProjectTitle, getTimeLeftLabel, isPhoneValid, getCanonicalURL,
  parseSearchFromUrl, isUrlActive, replaceTag, safe_tags_replace, getAPIQueryString,
  getLastResourceQuery, hasAPIQueryStringChanged, pwdStrengthMeter, preventDefault,
  handleIosModalsOverlap, removeExtraSpaces, scrollToModalTop, atAdmin
};

/* Creates the settings for time throughout the site */
moment.updateLocale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: 'a few seconds',
    m: '1m',
    mm: '%dm',
    h: '1h',
    hh: '%dh',
    d: '1d',
    dd: '%dd',
    M: '1m',
    MM: '%dmos',
    y: '1y',
    yy: '%dy'
  }
});




// WEBPACK FOOTER //
// ./common/utils.jsx